// Generated by GitHub Copilot
import { Api, MAX_PAGE_SIZE } from '#core/api/Api.ts'
import UserCompany from '#core/entities/auth/users/UserCompany.ts'
import UserCreate from '#core/entities/auth/users/UserCreate.ts'
import type { Permission } from '#core/entities/auth/roles/Permission.ts'
import type { BaseIdModel } from '#core/entities/shared/BaseModels.ts'
import { QueryResult } from '#core/entities/shared/Query.ts'

/**
 * Company-scoped user management — separate from `UserApi` (which handles "me" operations).
 * All endpoints require `AUTH_USERS` permission.
 */
export default class CompanyUsersApi extends Api<CompanyUsersApi> {
  constructor() {
    super()
  }

  /**
   * Returns users in the current company (management browse list).
   *
   * The endpoint returns a `QueryResult` envelope; the raw response is unwrapped to
   * its `results` array.
   *
   * @param take - Maximum number of users to return (page size).
   */
  async listUsers(take: number = MAX_PAGE_SIZE): Promise<UserCompany[]> {
    const result = await this.get<QueryResult<BaseIdModel>>('auth/users/', { take })
    return this.toQueryResult({ take }, result, UserCompany).results
  }

  /** Returns a single company user by ID. */
  async getUser(id: string): Promise<UserCompany | null> {
    return this.get<UserCompany>(`auth/users/${id}`, undefined, UserCompany)
  }

  /** Creates a new user and assigns them to the current company. */
  async createUser(data: UserCreate): Promise<UserCompany | null> {
    return this.post<UserCompany>('auth/users/', data, UserCompany)
  }

  /** Updates a user's profile (name, email). */
  async updateUser(id: string, data: { first_name?: string; last_name?: string; email?: string }): Promise<UserCompany | null> {
    return this.patch<UserCompany>(`auth/users/${id}`, data, UserCompany)
  }

  /** Replaces a user's assignment (role or personal permissions). */
  async updateAssignment(id: string, roleId?: string | null, personalPermissions?: Permission[] | null): Promise<void> {
    await this.patch(`auth/users/${id}/assignment`, {
      role_id: roleId,
      personal_permissions: personalPermissions,
    })
  }

  /** Enables or disables a member within the company; super users only (404 otherwise). */
  async toggleEnabled(id: string, enabled: boolean): Promise<void> {
    await this.patch(`auth/users/${id}/enabled`, { enabled })
  }

  /**
   * Sets whether a member must sign in with an MFA code and/or with SSO; an omitted
   * flag stays unchanged. Switching SSO on answers 422 while the company has no SSO connection.
   */
  async updateRequirements(id: string, mfaRequired?: boolean, ssoRequired?: boolean): Promise<UserCompany | null> {
    return this.patch<UserCompany>(`auth/users/${id}/requirements`, { mfa_required: mfaRequired, sso_required: ssoRequired }, UserCompany)
  }

  /** Sets a user's password. */
  async setPassword(id: string, password: string): Promise<void> {
    await this.patch(`auth/users/${id}/password`, { password })
  }

  /** Removes a user from the current company. */
  async removeUser(id: string): Promise<void> {
    await this.delete(`auth/users/${id}`)
  }
}
